<template>
  <div class="flex flex-col gap-8">
    <h3 class="v3-h4-semibold-600-line-height-1.5 text-[--color-neutral-black]">
      Popular Marketplaces
    </h3>

    <KeywordTableWrapper
      v-model:search="searchQuery"
      :is-free-plan="authStore.currentPlan === 'free'"
      :show-export="!!user.plan_id"
      :show-filter-chips="true"
      :show-match-filter="false"
      search-placeholder="Filter Marketplaces"
      :constraints-map="constraintsMap"
      :filter-constraints="marketplacesFilterConstraints"
      :remove-filter="removeFilter"
      :reset-filters="resetFilters"
      @export-panel-opened="handleExportPanelOpened"
    >
      <template #columns-panel="{ close }">
        <ColumnListSelector
          ref="columnListSelectorRef"
          :dictionary="columnPreferencesDictionary"
          :model-value="filteredPreferences"
          :saving="columnsStore.saving"
          @on-close="close"
          @update:model-value="(columns) => handleUpdateColumns(columns, close)"
        />
      </template>
    
      <template #export-panel>
        <ExportOptions
          :report-body="reportBody"
          :report-header="reportHeader"
          class="-mx-4 !border-none !p-0"
          :title="`eRank - Keyword Tool - ${searchParams.keyword} - Marketplaces`"
          page="Keyword Tool - Marketplaces"
        />
      </template>

      <!-- Slot for the table -->
      <slot />
    </KeywordTableWrapper>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';

import ColumnListSelector from '@/common/components/organisms/ColumnListSelector';
import ExportOptions from '@/common/components/organisms/ExportOptions';
import { useConstraintFilters } from '@/common/composables/constraintFilters';
import { useAuthStore } from '@/common/stores/auth';
import { useColumnPreferencesStore } from '@/common/stores/preferences';
import KeywordTableWrapper from '@/modules/keywords/components/organisms/KeywordTableWrapper';
import { useReport } from '@/modules/keywords/composables/report';
import { useSearchParams } from '@/modules/keywords/composables/router';
import { POPULAR_MARKETPLACES_TABLE_COLUMNS } from '@/modules/keywords/constants';
import { usePopularMarketplacesStore } from '@/modules/keywords/stores/keywords';
import type { ColumnPreferences } from '@/types/preferences';

// Column Selector
const columnsStore = useColumnPreferencesStore('keyword_tool_marketplaces_table_columns')();
columnsStore.setConfig({
  preferenceName: 'keyword_tool_marketplaces_table',
  defaultColumnPreferences: POPULAR_MARKETPLACES_TABLE_COLUMNS,
});

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const { preferences } = storeToRefs(columnsStore);
const { searchParams, updateSearchParams } = useSearchParams();
const marketplacesStore = usePopularMarketplacesStore();
const { marketplacesFilterConstraints, marketplaces } = storeToRefs(marketplacesStore);

const columnListSelectorRef = ref();

// Search functionality
const searchQuery = ref(searchParams.value.marketplaces?.query ?? '');

watch(searchQuery, (newValue) => {
  updateSearchParams({
    marketplaces: {
      query: newValue || undefined,
    },
  });
});

const filteredPreferences = computed(() => {
  return preferences.value;
});

// Dictionaries
const columnPreferencesDictionary = {
  search_trend: 'Search Trend',
  avg_searches: 'Avg. Searches',
  avg_clicks: 'Avg. Clicks',
  avg_ctr: 'Avg. CTR',
} as const;

const reportHeaderDictionary = {
  marketplace: 'Marketplace',
  search_trend: 'Search Trend',
  avg_searches: 'Average Searches',
  avg_clicks: 'Average Clicks',
  avg_ctr: 'CTR',
} as const;

const constraintsMap = {
  'avg_searches.order_value': 'Average Searches',
  'avg_clicks.order_value': 'Average Clicks',
  'ctr.order_value': 'CTR',
} as const;

const { reportBody, reportHeader } = useReport(marketplaces, filteredPreferences, reportHeaderDictionary);

const { resetFilters, removeFilter } = useConstraintFilters(marketplacesFilterConstraints);

async function handleUpdateColumns(columns: ColumnPreferences, close: () => void) {
  await columnsStore.saveColumnsPreferences(columns);
  close();
}

function handleExportPanelOpened() {
  // Track export panel opened if needed
}

onMounted(() => {
  columnsStore.getColumnsPreferences();
});
</script>
